/**
 * Read-only detail view for one client.
 *
 * The edit sheet answers "what can I change?"; this answers "who is this?".
 * Everything the record holds is on one screen — identity, subscription,
 * lifecycle dates, ownership, add-ons, the open flags and the notes — grouped
 * so it reads top to bottom rather than as a wall of form controls.
 */
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { stamp as demoStamp } from '../../domain/demos.js';
import { useApp } from '../../store/AppContext.jsx';
import { isOffline } from '../../lib/storage.js';
import { periodLabel } from '../../lib/dates.js';
import Icon from '../../lib/icons.jsx';
import Sheet from '../Sheet.jsx';
import ClientStatement from './ClientStatement.jsx';
import DealJourney from '../DealJourney.jsx';
import LeadCapture from '../LeadCapture.jsx';
import { clientOfDeal, leadDetails } from '../../domain/crm.js';
import {
  Avatar, TypePill, PlanPill, PayPill, StatusPill, OwnerPill, Pill, Dash, EmptyState, RiskBar
} from '../Primitives.jsx';
import {
  moduleList, isBundled, modulePrice, addonMonthly, amountOf, amountKnown, isBilled, mrrOf, cycleMonths
} from '../../domain/pricing.js';
import AskAI from "../AskAI.jsx";

/** One label/value line. Values fall back to a dash so rows never collapse. */
const Row = ({ label, children }) => (
  <div className="vrow">
    <span className="vlab">{label}</span>
    <span className="vval">{children ?? <Dash />}</span>
  </div>
);

const Panel = ({ icon, tone, title, children, wide }) => (
  <section className={`vpanel${wide ? ' wide' : ''}`}>
    <h4><span className={`ic ${tone}`}><Icon name={icon} /></span>{title}</h4>
    <div className="vbody">{children}</div>
  </section>
);

/** Severity 3 reads as urgent, 2 as a warning, 1 as advisory. */
const FLAG_TONE = { 3: 'gr-red', 2: 'gr-amber', 1: 'gr-slate' };

export default function ClientView({ id, onClose }) {
  const { rows, derive, S, crm, fmtMoney, fmtDate, setSheet, demos } = useApp();
  const r = rows.find(x => x.id === id);
  const navigate = useNavigate();

  /* Every demo this client booked: by the link the sync made, or by the
     same email where the link is to the clients app's own id. */
  const demoRows = useMemo(() => {
    if (!r || !demos || !demos.bookings) return [];
    const email = String(r.email || '').trim().toLowerCase();
    return demos.bookings
      .filter(b => (b.session && b.session.clientId && String(b.session.clientId) === String(r.id))
        || (email && String(b.invitee.email || '').toLowerCase() === email))
      .sort((a, b) => new Date(b.start) - new Date(a.start));
  }, [r, demos]);

  /*
   * The deal this account came in on, if it came in on one.
   *
   * An account that walked in off the website has nothing behind it and gets
   * none of these panels — an empty "Page journey" saying nothing happened
   * would be a lie about a visitor the tracker never saw. One that was worked
   * on the pipeline and moved to Deals carries the whole of it: what the
   * tracker saw, which pages, and every call anybody logged. That is the
   * record of how this client was won, and it was on a board they have since
   * left.
   *
   * Matched the way the rest of the app matches the two — the link the
   * conversion made, and the address where there is no link.
   */
  const fromDeal = useMemo(() => {
    if (!r) return null;
    const hit = (crm.deals || []).find(x => clientOfDeal(rows, x)?.id === r.id);
    if (hit) return hit;
    const email = String(r.email || '').trim().toLowerCase();
    if (!email) return null;
    return (crm.deals || []).find(x => {
      const f = leadDetails(crm, x);
      return String(f.email || '').trim().toLowerCase() === email;
    }) || null;
  }, [r, rows, crm]);

  const dealIntel = fromDeal ? leadDetails(crm, fromDeal) : null;

  /* B2B partner or B2C customer, from the demos this account has had. The
     latest one wins: a customer who has become a partner is a partner. */
  const partnerType = useMemo(() => {
    const hit = demoRows.find(b => b.session && b.session.customerType);
    return hit ? hit.session.customerType : '';
  }, [demoRows]);

  if (!r) {
    return (
      <Sheet icon="eye" title="Client" onClose={onClose} closeLabel="Close">
        <div style={{ gridColumn: '1/-1' }}>
          <EmptyState icon="alert" tone="g-red" title="That client is no longer in the list">
            It may have been deleted, or filtered out.
          </EmptyState>
        </div>
      </Sheet>
    );
  }

  const d = derive(r);
  const mods = moduleList(r);
  const bundled = isBundled(S, r);
  const recurring = isBilled(S, r) || r.amount;

  /*
   * Only tiles that actually hold a figure. A free account has no amount, no
   * MRR, no due date and no expiry, and four dashes in a row say nothing that
   * the panels below do not already say.
   */
  /* The monthly value only earns a tile when the bill is not already monthly —
     otherwise it repeats the amount beside it. A next due date only means
     something when there is a bill: a free account rolls one forward from its
     sign-up every year and would announce a payment nobody is going to make. */
  const monthly = mrrOf(S, r, d);
  const tiles = [
    recurring && amountKnown(S, r) && [fmtMoney(amountOf(S, r)), `Amount${periodLabel(r.cycle) || ''}`],
    monthly > 0 && cycleMonths(r.cycle) > 1 && [fmtMoney(monthly), 'Monthly value'],
    recurring && d.next && [fmtDate(d.next),
      `Next due${d.dueIn !== null ? ` · ${d.dueIn < 0 ? `${Math.abs(d.dueIn)}d late` : `in ${d.dueIn}d`}` : ''}`],
    d.exp && [fmtDate(d.exp),
      `Expires${d.dE !== null ? ` · ${d.dE < 0 ? `${Math.abs(d.dE)}d ago` : `in ${d.dE}d`}` : ''}`]
  ].filter(Boolean);

  const hero = (
    <div className="vhero">
      <div className="vhero-in">
        <Avatar name={r.name} className="vava" />
        <div className="vwho">
          <b>
            {r.name || 'Untitled'}
            {partnerType ? (
              <Pill tone={partnerType === 'B2B' ? 'gr-indigo' : 'gr-cyan'} className="vpartner">
                {partnerType === 'B2B' ? 'B2B partner' : 'B2C customer'}
              </Pill>
            ) : null}
          </b>
          <span>{r.email || 'no email on record'}</span>
        </div>
      </div>

      {tiles.length ? (
        <div className="vstats">
          {tiles.map(([value, label]) => (
            <div className="vstat" key={label}>
              <span>{value}</span>
              <small>{label}</small>
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );

  return (
    <Sheet
      icon="eye"
      tone="g-cyan"
      title="Client details"
      onClose={onClose}
      closeLabel="Close"
      bodyClass="viewbody"
      wide
      hero={hero}
      footerLeft={
        <>
          {/* A trial and a paying customer are the same record asked two
              different questions: one is 'will they convert', the other is
              'are we about to lose them'. Which one is asked follows the
              record rather than the page it was opened from. */}
          <AskAI
            module={d.dTrial === null ? 'client' : 'trial'}
            id={r.id}
            subject={r.name}
            label={d.dTrial === null ? 'Read this account' : 'Coach this trial'}
          />
          <button type="button" className="btn primary" onClick={() => setSheet({ kind: 'client', id: r.id })}>
            <Icon name="edit" />Edit client
          </button>
        </>
      }
    >
      {d.flags.length ? (
        <Panel icon="alert" tone="g-red" title={`Needs attention (${d.flags.length})`} wide>
          <div className="vflags">
            {d.flags.map(f => (
              <Pill key={f.tag} tone={FLAG_TONE[f.sev] || 'gr-slate'} icon={f.sev === 3 ? 'alert' : 'flag'}>
                {f.txt}
              </Pill>
            ))}
          </div>
        </Panel>
      ) : null}

      <Panel icon="users" tone="g-purple" title="Contact">
        <Row label="Client">{r.name || null}</Row>
        <Row label="Contact person">{r.contact || null}</Row>
        <Row label="Email">{r.email ? <a href={`mailto:${r.email}`}>{r.email}</a> : null}</Row>
        <Row label="Phone">{r.phone ? <a href={`tel:${r.phone}`}>{r.phone}</a> : null}</Row>
        <Row label="Source">{r.source || null}</Row>
      </Panel>

      {/*
        * The partner who brought them in.
        *
        * Its own panel rather than two lines under Contact, because it is
        * five facts about somebody who is not the customer — a second
        * relationship sitting behind this one — and reading them mixed in
        * with the customer's own phone number invites the wrong person being
        * rung.
        *
        * Drawn only where there is a partner. Nearly every account came in
        * directly, and an empty Partner panel on thirteen hundred records
        * says nothing thirteen hundred times.
        */}
      {r.partner ? (
        <Panel icon="users" tone="g-pink" title="Partner">
          <Row label="Partner name">{r.partner}</Row>
          <Row label="Partner email">
            {r.partnerEmail ? <a href={`mailto:${r.partnerEmail}`}>{r.partnerEmail}</a> : null}
          </Row>
          {/* The partner's three codes, named. They are the same on every
              account of theirs; what differs is the last row. */}
          <Row label="Referral code">{r.partnerReferral || null}</Row>
          <Row label="Promo code">{r.partnerPromo || null}</Row>
          <Row label="Tracking code">{r.partnerTracking || null}</Row>
          {/* And the one that is about this customer: which of the three
              they actually signed up on. */}
          <Row label="Signed up on">
            {r.partnerVia
              ? <>{r.partnerVia}{r.partnerWay ? <small className="dim">{` · their ${r.partnerWay}`}</small> : null}</>
              : null}
          </Row>
        </Panel>
      ) : null}

      <Panel icon="card" tone="g-green" title="Subscription">
        <Row label="Plan"><PlanPill value={r.plan} /></Row>
        <Row label="Type"><TypePill value={d.type} /></Row>
        <Row label="Payment"><PayPill value={r.pay} paidValues={S.paidValues} /></Row>
        <Row label="Billing cycle">{r.cycle || null}</Row>
        <Row label="Promo">{r.promo || null}</Row>
      </Panel>

      <Panel icon="cal" tone="g-blue" title="Lifecycle">
        {/* derive() hands back parsed Dates; formatDate calls .getDate()
            straight off its argument, so the raw string would throw. */}
        <Row label="Registered">{d.reg ? fmtDate(d.reg, true) : null}</Row>
        <Row label="Trial ends">
          {d.noTrial
            ? <Pill tone="gr-slate">No trial</Pill>
            : d.trial
              ? `${fmtDate(d.trial)}${d.dT !== null ? ` · ${d.dT < 0 ? `${Math.abs(d.dT)} days over` : `${d.dT} days left`}` : ''}`
              : null}
        </Row>
        <Row label="Expiry">{d.exp ? fmtDate(d.exp) : null}</Row>
        <Row label="Last paid">{r.lastPaid || null}</Row>
        <Row label="Last activity">{d.act ? fmtDate(d.act) : null}</Row>
        <Row label="Last contacted">{d.con ? fmtDate(d.con) : null}</Row>
      </Panel>

      <Panel icon="star" tone="g-cyan" title="Add-ons">
        {bundled ? (
          <div className="vflags"><Pill tone="gr-luxe" icon="star">All modules included</Pill></div>
        ) : mods.length ? (
          <>
            <div className="vmods">
              {mods.map(m => (
                <span className="vmod" key={m}>
                  <Icon name="check" />
                  <b>{m.replace('XYRA ', '')}</b>
                  {modulePrice(S, m) ? <i>{fmtMoney(modulePrice(S, m))}</i> : null}
                </span>
              ))}
            </div>
            {addonMonthly(S, r)
              ? <Row label="Add-on total">{`${fmtMoney(addonMonthly(S, r))} / month`}</Row>
              : null}
          </>
        ) : <div className="vnone">No add-ons on this account.</div>}
      </Panel>

      <Panel icon="users" tone="g-orange" title="Ownership">
        <Row label="Owner"><OwnerPill name={r.handler} /></Row>
        <Row label="Status"><StatusPill value={r.status} /></Row>
        {typeof r.aiRisk === 'number' ? (
          <>
            <Row label="AI risk">
              <RiskBar value={r.aiRisk} />
            </Row>
            {/* The list shows the bar alone; this is where the wording behind
                it belongs, including the step the scan suggests. */}
            {r.aiReason ? <Row label="Why">{r.aiReason}</Row> : null}
            {r.aiAction ? <Row label="Next step">{r.aiAction}</Row> : null}
          </>
        ) : null}
      </Panel>

      <Panel icon="file" tone="g-slate" title="Notes" wide>
        {r.notes ? <p className="vnotes">{r.notes}</p> : <div className="vnone">Nothing recorded yet.</div>}
      </Panel>

      {/*
        * How this one was won, when it was won rather than walked in.
        *
        * Both panels are the ones the pipeline row and the lead view show,
        * reading the same `lead_intel` and the same activity trail — so the
        * account, the lead and the deal all answer "what happened with this
        * one" with the same words. Neither appears on an account with no
        * deal behind it.
        */}
      {dealIntel && (dealIntel.sessions || []).length ? (
        <Panel icon="flow" tone="g-blue" title="What the tracker saw" wide>
          <LeadCapture f={dealIntel} name={fromDeal.title} fmtDate={fmtDate} />
        </Panel>
      ) : null}

      {fromDeal ? (
        <Panel icon="zap" tone="g-purple" title="How it was won" wide>
          <DealJourney
            deal={fromDeal}
            onEdit={actId => { onClose(); setSheet({ kind: 'activity', id: actId }); }}
          />
        </Panel>
      ) : null}

      {demos ? (
        <Panel icon="cal" tone="g-cyan" title={`Demos (${demoRows.length})`} wide>
          {demoRows.length ? (
            <div className="vdemos">
              {demoRows.map(b => {
                const s = b.session || {};
                const state = b.status === 'canceled' ? 'Cancelled'
                  : s.attendance === 'no_show' || b.noShow ? 'No-show'
                    : new Date(b.end) < new Date() ? (s.outcomeLabel || 'Completed · outcome not recorded')
                      : 'Upcoming';
                return (
                  <div className="vdemo" key={b.id}>
                    <span>{demoStamp(b.start)}</span>
                    <div>
                      <b>{state}</b>
                      <small>
                        {s.nextAction ? `→ ${s.nextAction}${s.nextDue ? ` by ${s.nextDue}` : ''}` : b.eventTypeName}
                        {s.ownerName ? ` · ${s.ownerName}` : ''}
                      </small>
                    </div>
                    <button type="button" className="btn sm" onClick={() => { onClose(); navigate('/demo-bookings', { state: { open: s.id } }); }}>Open</button>
                  </div>
                );
              })}
            </div>
          ) : <div className="vnone">No demo booked on Calendly with this email.</div>}
        </Panel>
      ) : null}

      {!isOffline() ? (
        <Panel icon="chart" tone="g-pink" title="Statement" wide>
          <ClientStatement id={r.id} />
        </Panel>
      ) : null}
    </Sheet>
  );
}
